import React, { useEffect, useMemo, useState } from "react";
import { Button, Modal, Form, Input, Select, InputNumber, Tag, Space, message, Typography, Card, Tabs, Tooltip, Segmented, Popconfirm, Dropdown } from "antd";
import Alert from "../components/GxAlert";
import { PlusOutlined, EditOutlined, SearchOutlined, HistoryOutlined, MergeCellsOutlined, FilePdfOutlined } from "@ant-design/icons";
import api, {
  getEmployees, createEmployee, updateEmployee, deleteEmployee, getDepartments,
  uploadEmployeePhoto, terminateEmployees, downloadFile,
} from "../api/client";
import EmployeePhoto from "../components/EmployeePhoto";
import BankAccountsPanel from "../components/BankAccountsPanel";
import IbanHint from "../components/IbanHint";
import usePdfPreview from "../utils/usePdfPreview";
import ImportExportBar from "../components/ImportExportBar";
import EditableTable, { CreatableSelect } from "../components/EditableTable";
import ExportButton from "../components/ExportButton";
import DismissibleHelp from "../components/DismissibleHelp";
import GxTabs from "../components/GxTabs";
import PayRolesEditor from "./employees/PayRolesEditor";
import CollaborationPicker from "../components/CollaborationPicker";
import NewStaffTag from "../components/NewStaffTag";
import MergeDuplicatesModal from "../components/MergeDuplicatesModal";
import { COLLAB_ITEMS, checkedOf, conflictOf, patchFor, tagsOf } from "../utils/collaboration";
import { byNum, byStr, byCode } from "../utils/sorters";
import { MoneyInput, formatMoney } from "../utils/money";
import { toJalali, fmtNum, JalaliDateInput } from "../utils/dateUtils";

/* آدرسِ API از کلاینتِ مشترک (client.js) — قبلاً اینجا نمونهٔ محلیِ axios با
   پورتِ ۸۰۰۰ هاردکد بود و روی HTTPS (پورتِ بسته) «خطا در بارگذاری» می‌داد. */
const { Title } = Typography;
const { Option } = Select;

const TYPE_OPTS = [
  { value: "official", label: "رسمی" },
  { value: "unofficial", label: "غیررسمی" },
];
/** فیلدهای اجباریِ هر تب — برای پریدن به تبِ فیلدِ خطادار هنگام ذخیره */
const TAB_OF_FIELD = {
  pay_model: "pay", has_insurance: "pay", monthly_salary: "pay",
  daily_rate: "pay", base_supplement: "pay", attendance_required: "pay",
  payroll_excluded: "pay", piece_pay_enabled: "pay",
  account_holder: "bank", bank_name: "bank", iban: "bank",
  card_number: "bank", account_number: "bank", device_user_id: "bank",
  nationality: "extra", identity_doc: "extra", phone: "extra", address: "extra",
  father_name: "extra", birth_date: "extra", id_number: "extra", marital_status: "extra",
  education: "extra", children_count: "extra", emergency_contact_name: "extra",
  emergency_contact_phone: "extra", insurance_number: "extra",
};

/** بانک‌های رایج — نقطهٔ شروع، نه فهرستِ بسته. */
const COMMON_BANKS = ["ملی", "ملت", "صادرات", "تجارت", "رفاه", "سپه", "کشاورزی",
  "مسکن", "پارسیان", "پاسارگاد", "آینده", "اقتصاد نوین", "سامان", "سینا", "شهر",
  "دی", "ایران زمین", "قوامین", "انصار", "حکمت ایرانیان", "گردشگری", "خاورمیانه",
  "کارآفرین", "سرمایه", "توسعه تعاون", "پست بانک", "مهر اقتصاد", "رسالت", "بلو",
  "بانک مرکزی"];

// ورودیِ تاریخِ داخلِ Modal.confirm — state خودش را دارد (محتوای confirm با state صفحه دوباره رندر نمی‌شود)؛
// zIndex بالا چون پنجرهٔ تأیید روی مودالِ پرسنل باز می‌شود
function PickedDate({ initial, onChange }) {
  const [v, setV] = useState(initial);
  return <JalaliDateInput value={v} zIndex={3000} onChange={(x) => { setV(x); onChange(x); }} />;
}

export default function EmployeesPage() {
  // بازهٔ نقش/نرخِ یک نفر (تغییرِ سمت وسطِ ماه)
  // پیش‌نمایشِ فرمِ PDF قبل از دانلود/چاپ
  const pdf = usePdfPreview();
  const formResolve = (r) => ({
    url: `/api/v1/employees/${r.id}/registration-form.pdf`,
    title: `فرم مشخصات و ثبت‌نام — ${r.full_name}`,
    fallbackName: `registration_${r.employee_code || r.id}.pdf`,
    onEdit: () => openEdit(r),
    editLabel: "ویرایش اطلاعات پرسنل",
  });
  // v2.159.0 (کاربر): «فرمِ ثبت‌نام دوتا بشه: همون، و یکی بیمه‌ای (پرسشنامهٔ تأمین اجتماعی)» — خام یا پرشده از پرونده
  const insFormResolve = (r) => ({
    url: `/api/v1/employees/${r.id}/insurance-form.pdf`,
    title: `پرسشنامه تأمین اجتماعی — ${r.full_name}`,
    fallbackName: `insurance_form_${r.employee_code || r.id}.pdf`,
    onEdit: () => openEdit(r),
    editLabel: "ویرایش اطلاعات پرسنل",
  });
  const formLabel = (e) => `${e.full_name}${e.employee_code ? ` (${e.employee_code})` : ""}`;
  // ناوبری بینِ نفراتِ **همین لیستِ فیلترشده** (به ترتیبِ کد) — اگر نفر در لیست نبود، کلِ پرسنل
  const previewForm = (rec) => {
    const list = visibleEmployees.some((e) => e.id === rec.id) ? visibleEmployees : employees;
    pdf.open({ items: list, index: Math.max(0, list.findIndex((e) => e.id === rec.id)),
      resolve: formResolve, labelOf: formLabel, storageKey: "registration", defaultPage: "A4" });
  };
  const previewInsForm = (rec) => {
    const list = visibleEmployees.some((e) => e.id === rec.id) ? visibleEmployees : employees;
    pdf.open({ items: list, index: Math.max(0, list.findIndex((e) => e.id === rec.id)),
      resolve: insFormResolve, labelOf: formLabel, storageKey: "insurance-form", defaultPage: "A4" });
  };
  const formMenu = (rec) => ({
    items: [{ key: "reg", label: "فرم مشخصات و ثبت‌نام" }, { key: "ins", label: "پرسشنامه تأمین اجتماعی (بیمه)" }],
    onClick: ({ key }) => (key === "ins" ? previewInsForm(rec) : previewForm(rec)),
  });
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  /* v2.152.12 (کاربر ۱۴۰۵/۰۸/۰۳): «ترک کار جدا از غیرفعال؛ ترک‌کارها بایگانی شوند و در لیست نباشند» ⇒ سه نما. */
  const [view, setView] = useState("active");      // active | inactive | archive | all
  const [selKeys, setSelKeys] = useState([]);
  const [termAsk, setTermAsk] = useState(null);    // {ids, date, reason}
  const isArchived = (e) => !!e.termination_date;
  const [editingId, setEditingId] = useState(null);
  // پرسنلی که «نوع همکاری»اش در حالِ ویرایشِ سریع از روی لیست است
  const [collabFor, setCollabFor] = useState(null);
  const [collabDraft, setCollabDraft] = useState(new Set());
  // چک‌لیستِ داخلِ فرمِ پرسنل — عمداً از draftِ مودالِ سریع جداست
  const [formCollab, setFormCollab] = useState(new Set());
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  // لیستِ روی صفحه (فیلترِ غیرفعال‌ها + جست‌وجو)، به ترتیبِ کد — منبعِ ناوبریِ پیش‌نمایشِ PDF هم هست
  const visibleEmployees = useMemo(() => employees
    .filter((e) => (view === "all"
        || (view === "archive" ? isArchived(e)
          : view === "inactive" ? (!isArchived(e) && e.status === "inactive")
            : (!isArchived(e) && e.status !== "inactive")))
      && (!searchText || e.full_name?.includes(searchText) || e.employee_code?.includes(searchText)
          || e.national_id?.includes(searchText) || e.phone?.includes(searchText)))
    .sort(byCode("employee_code")), [employees, view, searchText]);
  const viewCount = useMemo(() => ({
    active: employees.filter((e) => !e.termination_date && e.status !== "inactive").length,
    inactive: employees.filter((e) => !e.termination_date && e.status === "inactive").length,
    archive: employees.filter((e) => !!e.termination_date).length,
  }), [employees]);
  // جست‌وجوی مستقلِ تبِ بانکی — قبلاً این تب **هیچ** فیلتری نداشت و برای پیدا
  // کردنِ یک نفر باید کلِ ۱۰۰ ردیف را چشمی می‌گشتی.
  const [bankSearch, setBankSearch] = useState("");
  /* گزینه‌های بانک = فهرستِ رایج ∪ هر بانکی که واقعاً روی پرسنل ثبت شده.
     پس نامی که یک‌بار تایپ شود، دفعهٔ بعد خودش در فهرست هست. */
  /* فیلترِ تبِ بانکی — ارقامِ فارسی و جداکننده‌ها نرمال می‌شوند تا جست‌وجوی
     شماره کارت با «۶۰۶۲» یا «6062-5610» هر دو کار کند. */
  const bankRows = useMemo(() => {
    const q = String(bankSearch || "").trim();
    if (!q) return employees;
    const norm = (x) => String(x ?? "")
      .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
      .replace(/[^\w\u0600-\u06FF]/g, "").toLowerCase();
    const n = norm(q);
    if (!n) return employees;
    return employees.filter((e) => [e.full_name, e.employee_code, e.bank_name,
      e.account_holder, e.iban, e.card_number, e.account_number]
      .some((f) => norm(f).includes(n)));
  }, [employees, bankSearch]);

  const bankOptions = useMemo(() => {
    const seen = new Set(COMMON_BANKS);
    (employees || []).forEach((e) => {
      const b = String(e.bank_name || "").trim();
      if (b) seen.add(b);
    });
    return [...seen].sort((a, b) => a.localeCompare(b, "fa"))
      .map((b) => ({ value: b, label: b }));
  }, [employees]);
  const [modalTab, setModalTab] = useState("info");
  const [form] = Form.useForm();
  // عکسِ انتخاب‌شده برای پرسنلِ **جدید** — بعد از ساختنِ پرونده آپلود می‌شود
  const [pendingPhoto, setPendingPhoto] = useState(null);
  // آیا پرسنلِ در حالِ ویرایش عکس دارد (برای بارگذاریِ پیش‌نمایش)
  const [editHasPhoto, setEditHasPhoto] = useState(false);

  const [mergeOpen, setMergeOpen] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const [empRes, deptRes] = await Promise.all([getEmployees(), getDepartments()]);
      setEmployees(empRes.data);
      setDepartments(deptRes.data);
    } catch { message.error("خطا در بارگذاری"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const deptOpts = departments.map(d => ({ value: d.id, label: d.name }));

  const openAdd = () => {
    if (!departments.length) { message.warning("ابتدا بخش‌ها را ثبت کنید"); return; }
    form.resetFields();
    // پیش‌فرضِ متعارفِ کارخانه: حقوق ثابت + حضور و غیاب
    setFormCollab(new Set(["fixed", "attendance"]));
    setEditingId(null);
    setPendingPhoto(null);
    setEditHasPhoto(false);
    // 🐞 v2.151.1: اینجا `setModalTab(tab)` بود ولی `tab` در این تابع تعریف نشده ⇒ «tab is not defined» و دکمهٔ
    // «پرسنل جدید» هیچ کاری نمی‌کرد. پرسنلِ جدید همیشه از تبِ «اطلاعات پرسنلی» شروع می‌شود.
    setModalTab("info");
    setModalOpen(true);
  };

  const openEdit = (rec, tab = "info") => {
    form.setFieldsValue({ ...rec, department_id: rec.department?.id });
    setFormCollab(checkedOf(rec));
    setEditingId(rec.id);
    setPendingPhoto(null);
    setEditHasPhoto(!!rec.photo_path);
    setModalTab(tab);            // همان تبی که خواسته شده (مثلاً «اطلاعات بانکی»)؛ پیش‌فرض «info»
    setModalOpen(true);
  };

  /* تغییرِ کد/کارت ⇒ حضور و غیاب هم جابه‌جا می‌شود (v2.111.0). اینجا فقط «از چه تاریخی» پرسیده می‌شود:
     پیش‌فرض روزِ ورودِ نیروی تازه (۶۰ روزِ اخیر)، وگرنه امروز — همان قاعدهٔ `card_sync` در بک‌اند. */
  const cardChanged = (prev, next) => ["employee_code", "device_user_id"].some(
    (k) => k in next && String(next[k] ?? "").replace(/^0+/, "") !== String(prev?.[k] ?? "").replace(/^0+/, ""));
  const askCardFrom = (prev, next) => new Promise((resolve) => {
    const today = new Date().toISOString().slice(0, 10);
    const hire = next.hire_date || prev?.hire_date;
    const recent = hire && (Date.now() - new Date(hire).getTime()) / 864e5 <= 60 && hire <= today;
    let picked = recent ? hire : today;
    Modal.confirm({
      title: "کد/کارتِ دستگاه عوض شد — حضور و غیاب هم جابه‌جا شود",
      width: 480, okText: "ذخیره و جابه‌جایی", cancelText: "انصراف",
      content: (
        <div style={{ fontSize: 13 }}>
          ترددهای کارتِ تازه <b>از این تاریخ</b> به این نفر می‌رسد و ترددهای کارتِ قبلی‌اش از همین تاریخ به صاحبِ
          فعلیِ آن کارت (یا «بی‌صاحب» در کارت‌های دستگاه). سابقهٔ پیش از این تاریخ دست نمی‌خورد.
          <div style={{ marginTop: 10 }}>
            {/* 🐞 v2.152.7: تقویم زیرِ همین پنجره باز می‌شد (zIndex ۱۱۰۰ < پنجرهٔ تأییدِ رویِ مودالِ پرسنل) و
                تاریخِ انتخاب‌شده هم نمایش داده نمی‌شد (مقدارِ ثابت) ⇒ ورودیِ با state و zIndex بالاتر */}
            <PickedDate initial={picked} onChange={(v) => { picked = v; }} />
          </div>
        </div>),
      onOk: () => resolve({ ok: true, date: picked }),
      onCancel: () => resolve({ ok: false }),
    });
  });
  const showCardSync = (res) => {
    const m = res?.data?.card_sync_message;
    if (m) message.info({ content: m, duration: 8 });
  };

  const handleSave = async () => {
    try {
      const bad = conflictOf(formCollab);
      if (bad) { setModalTab("pay"); return message.error(bad); }
      const values = {
        ...(await form.validateFields()),
        // «نوع همکاری» به همان ستون‌های دیتابیس ترجمه می‌شود
        ...patchFor(formCollab, editingId ? employees.find((e) => e.id === editingId) : {}),
      };
      if (editingId) {
        const prev = employees.find((e) => e.id === editingId);
        let from;
        if (cardChanged(prev, values)) {
          const a = await askCardFrom(prev, values);
          if (!a.ok) return;
          from = a.date;
        }
        showCardSync(await updateEmployee(editingId, values, from));
        message.success("ویرایش شد");
      } else {
        const created = await createEmployee(values);
        message.success("ثبت شد");
        showCardSync(created);
        // عکسی که قبل از ساختنِ پرونده انتخاب شده بود، حالا آپلود می‌شود
        if (pendingPhoto && created?.data?.id) {
          try { await uploadEmployeePhoto(created.data.id, pendingPhoto); }
          catch (e) { message.warning(e?.response?.data?.detail || "پرونده ساخته شد ولی عکس ذخیره نشد"); }
        }
      }
      setPendingPhoto(null);
      setModalOpen(false);
      load();
    } catch (e) {
      // ردِّ validateFields شکلِ خطای axios را ندارد؛ قبلاً بی‌صدا رد می‌شد و فرم «کار نمی‌کرد»
      if (e?.errorFields?.length) {
        const first = e.errorFields[0].name;
        // فیلدِ خطادار ممکن است در تبِ دیگری باشد؛ اول تب عوض می‌شود بعد اسکرول
        setModalTab(TAB_OF_FIELD[String(first[0])] || "info");
        setTimeout(() => form.scrollToField(first, { behavior: "smooth", block: "center" }), 0);
        message.error("فیلدهای اجباری را کامل کنید");
        return;
      }
      const d = e?.response?.data?.detail;
      if (Array.isArray(d)) {
        // ۴۲۲ پایدانتیک: detail آرایه است و مستقیم قابلِ نمایش نیست
        message.error(d.map((x) => x?.msg || JSON.stringify(x)).join("؛ "));
      } else if (d) {
        message.error(d);
      } else {
        message.error("ذخیره ناموفق بود");
      }
    }
  };

  // inline save
  const handleInlineSave = async (id, changes) => {
    const prev = employees.find((e) => e.id === id);
    let from;
    if (cardChanged(prev, changes)) {
      const a = await askCardFrom(prev, changes);
      if (!a.ok) return;
      from = a.date;
    }
    showCardSync(await updateEmployee(id, changes, from));
    load();
  };

  /** حذف: تلاش برای حذفِ کامل؛ اگر سابقهٔ مالی داشت، غیرفعال‌سازی پیشنهاد می‌شود.
   *  قبلاً «حذف» فقط غیرفعال می‌کرد و شخص در لیست می‌ماند — کاربر فکر می‌کرد کار نکرده. */
  const handleDelete = async (id) => {
    try {
      const r = await deleteEmployee(id, { hard: true });
      message.success(r.data.message);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d) {
        const links = Object.entries(d.links || {})
          .map(([k, v]) => `${k}: ${fmtNum(v)}`).join("، ");
        Modal.confirm({
          title: d.message,
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>{d.hint}</p>
              <p style={{ fontSize: 12, color: "#8c8c8c" }}>سابقهٔ ثبت‌شده — {links}</p>
            </div>
          ),
          okText: "غیرفعال کن (سابقه می‌ماند)",
          cancelText: "انصراف",
          onOk: async () => {
            const rr = await deleteEmployee(id);
            message.success(rr.data.message);
            load();
          },
        });
      } else {
        message.error(typeof d === "string" ? d : "خطا در حذف");
      }
    }
  };

  // ── عملیاتِ دسته‌جمعی روی انتخاب‌شده‌ها (v2.152.12) ──
  const selectedEmps = employees.filter((e) => selKeys.includes(e.id));
  const doTerminate = async () => {
    if (!termAsk?.date) return message.warning("تاریخِ آخرین روزِ کار را بزنید");
    try {
      const r = await terminateEmployees({ ids: termAsk.ids, termination_date: termAsk.date, reason: termAsk.reason || null });
      message.success(r.data?.message || "ثبت شد");
      setTermAsk(null); setSelKeys([]); load();
    } catch (e) { message.error(e?.response?.data?.detail || "ثبت نشد"); }
  };
  const doUnterminate = async (ids) => {
    try {
      const r = await terminateEmployees({ ids, termination_date: null });
      message.success(r.data?.message || "برگشت به کار");
      setSelKeys([]); load();
    } catch (e) { message.error(e?.response?.data?.detail || "انجام نشد"); }
  };
  const doSetStatus = async (status) => {
    let ok = 0;
    for (const e of selectedEmps) {
      try { await updateEmployee(e.id, { status }); ok += 1; } catch { /* بقیه ادامه */ }
    }
    message.success(`${fmtNum(ok)} نفر ${status === "active" ? "فعال" : "غیرفعال"} شدند`);
    setSelKeys([]); load();
  };
  const doBulkDelete = async () => {
    const kept = [];
    let gone = 0;
    for (const e of selectedEmps) {
      try { await deleteEmployee(e.id, { hard: true }); gone += 1; } catch { kept.push(e.full_name); }
    }
    if (gone) message.success(`${fmtNum(gone)} نفر حذف شدند`);
    if (kept.length) {
      Modal.info({ title: `${fmtNum(kept.length)} نفر سابقه دارند و حذف نشدند`,
        content: <div>{kept.join("، ")}<p style={{ marginTop: 8, fontSize: 12, color: "#888" }}>
          برای این‌ها «ترک کار» یا «غیرفعال» بزنید — سابقهٔ حقوق و حضورشان می‌ماند.</p></div> });
    }
    setSelKeys([]); load();
  };
  const exportSelected = async () => {
    try {
      await downloadFile("/api/v1/export/employees", "لیست پرسنل (انتخاب‌شده).xlsx",
        { params: { employee_ids: selKeys.join(",") } });
      message.success(`فایلِ ${fmtNum(selKeys.length)} نفر دانلود شد`);
    } catch { message.error("خطا در ساختِ خروجی"); }
  };

  // import اکسل
  const handleImport = async (file) => {
    setImportLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/api/v1/employees/import-excel", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult({ message: res.data.message });
      load();
    } catch (e) {
      setImportResult({ type: "error", message: e?.response?.data?.detail || "خطا در import" });
    }
    setImportLoading(false);
  };

  const columns = [
    { title: "کد", dataIndex: "employee_code", width: 90, editType: "text",
      sorter: byCode("employee_code"), defaultSortOrder: "ascend" },
    { title: "نام", dataIndex: "full_name", editType: "text", width: 160, sorter: byStr("full_name"),
      render: (v, rec) => <span>{v}<NewStaffTag rec={rec} /></span> },
    { title: "بخش", dataIndex: "department_id", width: 130,
      editType: "select", options: deptOpts, sorter: byStr(["department", "name"]),
      render: (_, rec) => rec.department?.name || "—" },
    {
      // ⬇ یک ستون به‌جای «مدل حقوق + بیمه + حضوری + کارمزد دوخت + خارج از حقوق»
      title: "نوع همکاری", dataIndex: "pay_model", width: 240,
      sorter: byStr("pay_model"),
      // فیلترِ خودِ antd (چندانتخابی) — «همهٔ کسانی که کارمزدشان حساب می‌شود»
      filters: COLLAB_ITEMS.map((i) => ({ value: i.key, text: i.label })),
      onFilter: (val, row) => checkedOf(row).has(val),
      render: (_, rec) => (
        <Space size={2} wrap style={{ cursor: "pointer" }} onClick={() => { setCollabFor(rec); setCollabDraft(checkedOf(rec)); }}>
          {tagsOf(rec).map((t) => (
            <Tag key={t.key} color={t.color} style={t.secondary ? { opacity: 0.85 } : undefined}>
              {t.label}
            </Tag>
          ))}
          <Tooltip title="ویرایشِ نوع همکاری"><EditOutlined style={{ color: "#999" }} /></Tooltip>
        </Space>
      ),
    },
    /* ویرایشِ تکی — تا امروز فقط تبِ «اطلاعات بانکی» این دکمه را داشت و در
       لیستِ اصلی تنها راه، ویرایشِ درجا/دسته‌جمعی بود. برای عوض‌کردنِ یک
       فیلدِ فرمی (تاریخ استخدام، بیمه، سمت) باید می‌رفتی تبِ دیگر. */
    {
      title: "ویرایش", key: "__actions", width: 110, fixed: "left", gxExport: false,
      render: (_, rec) => (
        <Space size={4}>
          <Tooltip title="ویرایشِ کاملِ پرسنل">
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(rec)} />
          </Tooltip>
          <Tooltip title="نقش‌ها و بازه‌های حقوقی (روزمزد از یک تاریخ، سرایداری، …)">
            <Button size="small" icon={<HistoryOutlined />} onClick={() => openEdit(rec, "pay")} />
          </Tooltip>
          {/* فرمِ مشخصات/ثبت‌نامِ همین نفر — A4 با عکس و جای امضا */}
          <Tooltip title="فرم‌ها (PDF): مشخصات و ثبت‌نام / پرسشنامه تأمین اجتماعی">
            <Dropdown menu={formMenu(rec)} trigger={["click"]}>
              <Button size="small" icon={<FilePdfOutlined />} />
            </Dropdown>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "حقوق ثابت (ریال)", dataIndex: "monthly_salary", editType: "number", sorter: byNum("monthly_salary"),
      render: (v) => formatMoney(v),
    },
    // اطلاعاتِ پرسنلیِ فایلِ کارخانه — همه دستی قابلِ ویرایش‌اند
    { title: "سمت", dataIndex: "job_title", width: 130, editType: "text", sorter: byStr("job_title"),
      render: v => v || <span style={{color:"#ccc"}}>—</span> },
    // v2.132.0: ستونِ جدای «بیمه» — مرتب‌سازی/فیلتر (در «نوع همکاری» هم هست)
    { title: "بیمه", dataIndex: "has_insurance", width: 100,
      sorter: (a, b) => Number(!!b.has_insurance) - Number(!!a.has_insurance),
      filters: [{ value: true, text: "بیمه‌ای" }, { value: false, text: "غیربیمه‌ای" }],
      onFilter: (v, r) => !!r.has_insurance === v,
      render: (v) => <Tag color={v ? "blue" : "default"} style={{ margin: 0 }}>{v ? "بیمه‌ای" : "غیربیمه‌ای"}</Tag> },
    { title: "کد ملی / شناسه", dataIndex: "national_id", width: 140, editType: "text",
      render: v => v ? <code style={{fontSize:11}}>{v}</code> : <span style={{color:"#ccc"}}>—</span> },
    { title: "ملیت", dataIndex: "nationality", width: 100, editType: "select",
      options: [{ value: "ایرانی", label: "ایرانی" }, { value: "افغانستانی", label: "افغانستانی" }],
      render: v => v || <span style={{color:"#ccc"}}>—</span> },
    { title: "مدارک", dataIndex: "identity_doc", width: 110, editType: "text",
      render: v => v || <span style={{color:"#ccc"}}>—</span> },
    { title: "شماره تماس", dataIndex: "phone", width: 120, editType: "text",
      render: v => v ? <code style={{fontSize:11}}>{v}</code> : <span style={{color:"#ccc"}}>—</span> },
    { title: "آدرس", dataIndex: "address", width: 200, editType: "textarea",
      render: v => v || <span style={{color:"#ccc"}}>—</span> },
    { title: "تاریخ استخدام", dataIndex: "hire_date", width: 120, editType: "date",
      render: v => v ? toJalali(v) : <span style={{color:"#ccc"}}>—</span> },
    {
      title: "وضعیت", dataIndex: "status", width: 85, sorter: byStr("status"),
      editType: "select", options: [{ value: "active", label: "فعال" }, { value: "inactive", label: "غیرفعال" }],
      render: (v, r) => (r.termination_date
        ? <Tooltip title={r.termination_reason || ""}><Tag color="default">ترک کار {toJalali(r.termination_date)}</Tag></Tooltip>
        : <Tag color={v === "active" ? "green" : "red"}>{v === "active" ? "فعال" : "غیرفعال"}</Tag>),
    },
  ];

  const bankColumns = [
    { title: "کد", dataIndex: "employee_code", width: 80 },
    { title: "نام پرسنل", dataIndex: "full_name", width: 140 },
    { title: "نام صاحب حساب", dataIndex: "account_holder", editType: "text", render: v => v || <span style={{color:"#ccc"}}>—</span> },
    { title: "بانک", dataIndex: "bank_name", width: 100, editType: "text", render: v => v || <span style={{color:"#ccc"}}>—</span> },
    { title: "شماره شبا", dataIndex: "iban", editType: "text", render: v => v ? <code style={{fontSize:11}}>{v}</code> : <span style={{color:"#ccc"}}>—</span> },
    { title: "شماره کارت", dataIndex: "card_number", editType: "text", render: v => v ? <code style={{fontSize:11}}>{v}</code> : <span style={{color:"#ccc"}}>—</span> },
    /* بعضی بانک‌ها برای پرداختِ داخلی شماره حساب می‌خواهند نه شبا — و فایلِ
       پرداخت ستونش را دارد، پس باید جایی هم برای پرکردنش باشد. */
    { title: "شماره حساب", dataIndex: "account_number", editType: "text", render: v => v ? <code style={{fontSize:11}}>{v}</code> : <span style={{color:"#ccc"}}>—</span> },
    {
      title: "شناسه دستگاه", dataIndex: "device_user_id", width: 100, editType: "number",
      render: v => v != null ? <Tag color="blue">{v}</Tag> : <span style={{color:"#ccc"}}>—</span>,
    },
    {
      title: "ویرایش", width: 110,
      render: (_, rec) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(rec)} />
          <Tooltip title="فرم‌ها (PDF): مشخصات و ثبت‌نام / پرسشنامه تأمین اجتماعی">
            <Dropdown menu={formMenu(rec)} trigger={["click"]}>
              <Button size="small" icon={<FilePdfOutlined />} />
            </Dropdown>
          </Tooltip>
          {/* نقش‌ها و بازه‌ها: کسی که از یک تاریخ روزمزد شده، سرایداری، وسط‌کاری */}
          <Tooltip title="نقش‌ها و بازه‌های حقوقی">
            <Button size="small" icon={<HistoryOutlined />}
              onClick={() => openEdit(rec, "pay")} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <MergeDuplicatesModal open={mergeOpen} onClose={() => setMergeOpen(false)}
        onMerged={load} />
      {!loading && !departments.length && (
        <Alert type="warning" message='ابتدا از منوی «بخش‌ها» بخش‌های پیش‌فرض را ثبت کنید' showIcon style={{ marginBottom: 12 }} />
      )}

      <GxTabs urlKey="tab" defaultActiveKey="list" items={[
        {
          key: "list",
          label: `لیست پرسنل (${employees.length})`,
          children: (
            <>
              {/* v2.164.0 (کاربر): «سرچِ پرسنل بالا کوچک شده چون گزینه‌ها زیاد شد — بیاور پایین‌تر، فضای خالی بیشتر داریم».
                  ردیفِ ۱ = دکمه‌ها (می‌شکند)، ردیفِ ۲ = فعال/غیرفعال/بایگانی + جست‌وجوی پهن. */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                <Space wrap>
                  <ExportButton url="/api/v1/export/employees" label="لیست پرسنل" />
                  <Tooltip title="فرم‌های خام (A4) برای پرکردنِ دستی توسط پرسنلِ جدید">
                    <Dropdown trigger={["click"]} menu={{
                      items: [{ key: "reg", label: "فرم ثبت‌نام پرسنل (خام)" },
                              { key: "ins", label: "پرسشنامه تأمین اجتماعی — بیمه (خام)" }],
                      onClick: ({ key }) => pdf.open(key === "ins" ? {
                        url: "/api/v1/employees/insurance-form/blank.pdf",
                        title: "پرسشنامه تأمین اجتماعی (خام)", fallbackName: "insurance_form_blank.pdf",
                        storageKey: "insurance-form", defaultPage: "A4",
                      } : {
                        url: "/api/v1/employees/registration-form/blank.pdf",
                        title: "فرم ثبت‌نام پرسنل (خام)", fallbackName: "registration_form_blank.pdf",
                        storageKey: "registration", defaultPage: "A4",
                      }),
                    }}>
                      <Button icon={<FilePdfOutlined />}>فرم خام ▾</Button>
                    </Dropdown>
                  </Tooltip>
                  {/* یک نفر نباید دو پرونده داشته باشد؛ نقشِ دوم «شغلِ دوم» است نه پروندهٔ دوم */}
                  <Tooltip title="پیداکردن و یکی‌کردنِ پرونده‌های تکراری">
                    <Button icon={<MergeCellsOutlined />} onClick={() => setMergeOpen(true)}>
                      پرونده‌های تکراری
                    </Button>
                  </Tooltip>
                  <Tooltip title={!departments.length ? "ابتدا بخش‌ها را ثبت کنید" : ""}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} disabled={!departments.length}>
                      پرسنل جدید
                    </Button>
                  </Tooltip>
                </Space>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <Input
                  prefix={<SearchOutlined />}
                  placeholder="جستجوی نام، کد پرسنلی، کد ملی، شماره..."
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  allowClear
                  style={{ flex: "1 1 320px", minWidth: 220, maxWidth: 560 }}
                />
                <Segmented value={view} onChange={(v) => { setView(v); setSelKeys([]); }}
                  options={[
                    { value: "active", label: `فعال (${fmtNum(viewCount.active)})` },
                    { value: "inactive", label: `غیرفعال (${fmtNum(viewCount.inactive)})` },
                    { value: "archive", label: `بایگانی — ترک کار (${fmtNum(viewCount.archive)})` },
                    { value: "all", label: "همه" },
                  ]} />
              </div>
              {/* انتخابِ چک‌باکسی + عملیاتِ دسته‌جمعی (v2.152.12) */}
              {selKeys.length > 0 && (
                <Space wrap style={{ marginBottom: 8, padding: "6px 10px", borderRadius: 8,
                  background: "var(--surface-2, #f5f7fa)", border: "1px solid var(--border, #e4e8f0)" }}>
                  <b>{fmtNum(selKeys.length)} نفر انتخاب شده</b>
                  <Button size="small" onClick={exportSelected}>خروجیِ اکسلِ انتخاب‌شده‌ها</Button>
                  {view !== "archive" && (
                    <Button size="small" danger
                      onClick={() => setTermAsk({ ids: selKeys, date: null, reason: "" })}>ترک کار…</Button>)}
                  {view === "archive" && (
                    <Popconfirm title={`${fmtNum(selKeys.length)} نفر به کار برگردند؟`} okText="بله" cancelText="خیر"
                      onConfirm={() => doUnterminate(selKeys)}>
                      <Button size="small">برگشت به کار</Button>
                    </Popconfirm>)}
                  {view !== "archive" && <Button size="small" onClick={() => doSetStatus("inactive")}>غیرفعال</Button>}
                  {view === "inactive" && <Button size="small" onClick={() => doSetStatus("active")}>فعال</Button>}
                  <Popconfirm title={`حذفِ کاملِ ${fmtNum(selKeys.length)} نفر؟`}
                    description="فقط کسانی که هیچ سابقه‌ای ندارند حذف می‌شوند؛ بقیه می‌مانند."
                    okText="حذف" cancelText="انصراف" okButtonProps={{ danger: true }} onConfirm={doBulkDelete}>
                    <Button size="small" danger>حذف</Button>
                  </Popconfirm>
                  <Button size="small" type="link" onClick={() => setSelKeys([])}>لغوِ انتخاب</Button>
                  <span style={{ fontSize: 11, color: "#888" }}>
                    ویرایشِ یک ستون برای همه: «ویرایش» ⇐ «اعمال روی انتخاب‌شده‌ها»
                  </span>
                </Space>
              )}
              <EditableTable
                dataSource={visibleEmployees}
                columns={columns}
                onSave={handleInlineSave}
                onDelete={handleDelete}
                loading={loading}
                selectedRowKeys={selKeys}
                onSelectionChange={setSelKeys}
              />
              <Modal open={!!termAsk} title={`ترک کار — ${fmtNum(termAsk?.ids?.length || 0)} نفر`}
                okText="ثبتِ ترک کار" cancelText="انصراف" okButtonProps={{ danger: true }}
                onCancel={() => setTermAsk(null)} onOk={doTerminate} destroyOnClose>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <div style={{ fontSize: 12, color: "#888" }}>
                    {employees.filter((e) => termAsk?.ids?.includes(e.id)).map((e) => e.full_name).join("، ")}
                  </div>
                  <span>آخرین روزِ کار:</span>
                  <JalaliDateInput value={termAsk?.date} zIndex={3000}
                    onChange={(v) => setTermAsk((t) => ({ ...t, date: v }))} />
                  <Input placeholder="علت (اختیاری)" value={termAsk?.reason}
                    onChange={(e) => setTermAsk((t) => ({ ...t, reason: e.target.value }))} />
                  <div style={{ fontSize: 12, color: "#888" }}>
                    به بایگانی می‌روند و از لیستِ فعال و ماه‌های بعد بیرون می‌روند؛ حقوق و حضورِ ماهِ ترک کار فقط تا همین روز
                    حساب می‌شود. هر وقت لازم شد از «بایگانی» ⇐ «برگشت به کار».
                  </div>
                </Space>
              </Modal>
            </>
          ),
        },
        {
          key: "bank",
          label: "اطلاعات بانکی",
          children: (
            <>
              <DismissibleHelp
                id="emp-bank-tab"
                message="برای تکمیل اطلاعات بانکی هر پرسنل، روی دکمه ویرایش کلیک کنید و به تب «اطلاعات بانکی» بروید."
              />
              <Space wrap style={{ marginBottom: 4 }}>
                <ExportButton url="/api/v1/export/employees" label="خروجی کامل پرسنل" />
                {/* جست‌وجو شاملِ شبا/کارت/حساب هم هست: وقتی از بانک یک شماره
                    می‌آید و باید بفهمیم مالِ کیست، همین لازم است. */}
                <Input.Search allowClear style={{ width: 320 }}
                  placeholder="جستجوی نام، کد، بانک، صاحبِ حساب، شبا یا کارت…"
                  value={bankSearch} onChange={(e) => setBankSearch(e.target.value)} />
                <span style={{ fontSize: 12, color: "#888" }}>
                  {bankRows.length} از {employees.length} نفر
                </span>
              </Space>
              <div style={{ marginTop: 12 }}>
                <EditableTable
                  layoutId="employees-bank"
                  dataSource={bankRows}
                  columns={bankColumns}
                  onSave={handleInlineSave}
                  loading={loading}
                  pagination={{ pageSize: 20 }}
                />
              </div>
            </>
          ),
        },
        {
          key: "import",
          label: "ورود از اکسل",
          children: (
            <Card style={{ maxWidth: 520 }}>
              <ImportExportBar
                templateUrl="/api/v1/templates/employees"
                templateLabel="پرسنل"
                onImport={handleImport}
                loading={importLoading}
                result={importResult}
              />
            </Card>
          ),
        },
      ]} />

      {/* ویرایشِ سریعِ «نوع همکاری» بدونِ بازکردنِ کلِ فرمِ پرسنل */}
      <Modal open={!!collabFor} title={`نوع همکاری — ${collabFor?.full_name || ""}`}
        okText="ذخیره" cancelText="انصراف"
        okButtonProps={{ disabled: !!conflictOf(collabDraft) }}
        onCancel={() => setCollabFor(null)}
        onOk={async () => {
          try {
            await updateEmployee(collabFor.id, patchFor(collabDraft, collabFor));
            message.success("نوع همکاری ذخیره شد");
            setCollabFor(null);
            load();
          } catch (e) {
            message.error(e?.response?.data?.detail || "ذخیره ناموفق بود");
          }
        }}>
        <CollaborationPicker value={collabDraft} onChange={setCollabDraft}
          secondaryRoles={collabFor?.secondary_roles || []} />
      </Modal>

      <Modal title={editingId ? "ویرایش پرسنل" : "پرسنل جدید"} open={modalOpen}
        onOk={handleSave} onCancel={() => setModalOpen(false)} okText="ذخیره" cancelText="انصراف"
        width={modalTab === "pay" ? 880 : 640} style={{ top: 20 }}>
        <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
          <GxTabs activeKey={modalTab} onChange={setModalTab} size="small" items={[
            {
              key: "info",
              label: "اطلاعات پرسنلی",
              // forceRender: بدونِ آن، فیلدهای تبِ دیده‌نشده اصلاً به Form ثبت نمی‌شوند و
              // اعتبارسنجی بی‌صدا رد می‌شود ولی payload ناقص می‌رود (باگِ «ذخیره نمی‌شود»)
              forceRender: true,
              children: (
                <>
                  <EmployeePhoto employeeId={editingId} hasPhoto={editHasPhoto}
                    onChanged={(has) => { setEditHasPhoto(has); load(); }}
                    onPending={setPendingPhoto} />
                  <Form.Item name="employee_code" label="کد پرسنلی" extra="اختیاری — فقط تکراری‌بودن مجاز نیست"><Input /></Form.Item>
                  <Form.Item name="full_name" label="نام کامل" rules={[{ required: true }]}><Input /></Form.Item>
                  <Form.Item name="national_id" label="کد ملی"><Input maxLength={10} /></Form.Item>
                  <Form.Item name="gender" label="جنسیت" rules={[{ required: true }]}>
                    <Select><Option value="female">زن</Option><Option value="male">مرد</Option></Select>
                  </Form.Item>
                  <Form.Item name="department_id" label="بخش" rules={[{ required: true }]}>
                    <Select>{departments.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}</Select>
                  </Form.Item>
                  <Form.Item name="job_title" label="عنوان شغلی"><Input /></Form.Item>
                  <Form.Item name="employment_type" label="نوع استخدام" initialValue="official">
                    <Select><Option value="official">رسمی</Option><Option value="unofficial">غیررسمی</Option></Select>
                  </Form.Item>
                  <Form.Item name="hire_date" label="تاریخ استخدام" rules={[{ required: true }]}>
                    <JalaliDateInput style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item name="status" label="وضعیت" initialValue="active">
                    <Select><Option value="active">فعال</Option><Option value="inactive">غیرفعال</Option></Select>
                  </Form.Item>
                  {/* v2.152.12 ترک کار — جدا از «غیرفعال»؛ پر ⇒ بایگانی و حقوق/حضور فقط تا همین روز */}
                  <Form.Item name="termination_date" label="تاریخ ترک کار (آخرین روزِ کار)"
                    extra="خالی = هنوز کار می‌کند. پر کردن ⇒ بایگانی؛ پاک کردن ⇒ برگشت به کار.">
                    <JalaliDateInput style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item name="termination_reason" label="علتِ ترک کار"><Input /></Form.Item>
                </>
              ),
            },
            {
              key: "pay",
              label: "اطلاعات حقوقی",
              forceRender: true,
              children: (
                <>
                  {/* یک چک‌لیست به‌جای «مدل حقوق + بیمه + حضوری + کارمزد + خارج از حقوق» */}
                  <Card size="small" title="نوع همکاری" style={{ marginBottom: 16 }}>
                    <CollaborationPicker value={formCollab} onChange={setFormCollab} />
                  </Card>
                  <Form.Item name="monthly_salary" label="حقوق ثابت ماهانه (ریال)">
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item name="daily_rate" label="نرخ روزانه (ریال)">
                    <MoneyInput />
                  </Form.Item>
                  <Form.Item name="base_supplement" label="پایه تکمیلی / حداقل ضمانت (ریال)">
                    <MoneyInput />
                  </Form.Item>
                  {/* «شغل‌های دیگر» و «بازه‌های نقش» اینجا یکی شدند (v2.108.0) */}
                  <Card size="small" title="نقش‌ها و بازه‌ها (تغییرِ مدل از یک تاریخ، نقشِ اضافه)">
                    <PayRolesEditor employeeId={editingId}
                      hireDate={editingId ? employees.find((e) => e.id === editingId)?.hire_date : null} />
                  </Card>
                </>
              ),
            },
            {
              key: "extra",
              label: "هویتی و تماس",
              forceRender: true,
              children: (
                <>
                  <Alert type="info" showIcon style={{ marginBottom: 12 }}
                    message="این‌ها در «فرم مشخصات و ثبت‌نام» (PDF) چاپ می‌شوند." />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                    <Form.Item name="father_name" label="نام پدر"><Input /></Form.Item>
                    <Form.Item name="birth_date" label="تاریخ تولد"><JalaliDateInput style={{ width: "100%" }} /></Form.Item>
                    <Form.Item name="id_number" label="شمارهٔ شناسنامه"><Input maxLength={30} /></Form.Item>
                    <Form.Item name="marital_status" label="وضعیت تأهل">
                      <Select allowClear><Option value="single">مجرد</Option><Option value="married">متأهل</Option></Select>
                    </Form.Item>
                    <Form.Item name="education" label="تحصیلات"><Input /></Form.Item>
                    <Form.Item name="children_count" label="تعداد فرزند"><InputNumber min={0} max={20} style={{ width: "100%" }} /></Form.Item>
                    <Form.Item name="nationality" label="ملیت"><Input /></Form.Item>
                    <Form.Item name="identity_doc" label="نوع مدرک"><Input placeholder="کارت ملی / آمایش / گذرنامه" /></Form.Item>
                    <Form.Item name="phone" label="شماره تماس"><Input maxLength={20} style={{ fontFamily: "monospace" }} /></Form.Item>
                    <Form.Item name="insurance_number" label="شمارهٔ بیمه"><Input maxLength={40} /></Form.Item>
                    <Form.Item name="emergency_contact_name" label="فرد اضطراری (نام)"><Input /></Form.Item>
                    <Form.Item name="emergency_contact_phone" label="فرد اضطراری (تلفن)"><Input maxLength={30} style={{ fontFamily: "monospace" }} /></Form.Item>
                  </div>
                  <Form.Item name="address" label="نشانی محل سکونت"><Input.TextArea rows={2} maxLength={300} /></Form.Item>
                </>
              ),
            },
            {
              key: "bank",
              label: "اطلاعات بانکی",
              forceRender: true,
              children: (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="این اطلاعات در لیست پرداخت حسابداری استفاده می‌شود"
                    style={{ marginBottom: 16 }}
                  />
                  <Form.Item name="account_holder" label="نام صاحب حساب (ممکن است با نام پرسنل متفاوت باشد)">
                    <Input placeholder="مثال: علی محمدی" />
                  </Form.Item>
                  {/* فهرستِ بسته نبود کارساز: بانکی که در فهرست نبود اصلاً
                      قابلِ ثبت نبود. حالا هم می‌شود **خالی** گذاشت (بانکِ
                      بعضی‌ها را نمی‌دانیم) و هم نامِ تازه تایپ کرد — که از
                      دفعهٔ بعد خودش در فهرست می‌آید، چون گزینه‌ها از خودِ
                      پرسنلِ ثبت‌شده هم ساخته می‌شوند. */}
                  <Form.Item name="bank_name" label="نام بانک (اختیاری)">
                    <CreatableSelect allowClear options={bankOptions}
                      placeholder="انتخاب یا تایپِ نامِ بانک — خالی هم اشکالی ندارد" />
                  </Form.Item>
                  <Form.Item name="iban" label="شماره شبا"
                    extra={<Form.Item noStyle shouldUpdate={(a, b) => a.iban !== b.iban || a.bank_name !== b.bank_name}>
                      {({ getFieldValue }) => <IbanHint iban={getFieldValue("iban")} bank={getFieldValue("bank_name")}
                        onBank={(bk) => !getFieldValue("bank_name") && form.setFieldsValue({ bank_name: bk })} />}
                    </Form.Item>}>
                    <Input
                      placeholder="IR000000000000000000000000"
                      maxLength={26}
                      style={{ fontFamily: "monospace", letterSpacing: 2 }}
                    />
                  </Form.Item>
                  <Form.Item name="card_number" label="شماره کارت">
                    <Input
                      placeholder="0000-0000-0000-0000"
                      maxLength={19}
                      style={{ fontFamily: "monospace", letterSpacing: 2 }}
                    />
                  </Form.Item>
                  {/* v2.157.0 (کاربر): «شماره حساب برای کلِ پرسنل، اختیاری — برای مفاصا و لیستِ بیمه» */}
                  <Form.Item name="account_number" label="شماره حساب (اختیاری)"
                    extra="برای مفاصا حساب و لیستِ بیمه؛ جدا از شبا و کارت">
                    <Input placeholder="مثلاً 0123456789" maxLength={40}
                      style={{ fontFamily: "monospace", letterSpacing: 1 }} />
                  </Form.Item>
                  {/* v2.160.0: فیلدهای بالا = حسابِ اصلی (پرداخت‌ها)؛ حساب‌های دیگر (مثلاً سامانِ بیمه) اینجا */}
                  <BankAccountsPanel employeeId={editingId}
                    onPrimaryChanged={(p) => form.setFieldsValue({
                      bank_name: p.bank_name, iban: p.iban, card_number: p.card_number,
                      account_number: p.account_number, account_holder: p.account_holder,
                    })} />
                  <Form.Item
                    name="device_user_id"
                    label="شناسه دستگاه حضور و غیاب"
                    extra="شناسه ثبت‌شده برای این فرد در دستگاه اثرانگشت — برای تطبیق رکوردهای حضور"
                  >
                    <InputNumber style={{ width: "100%" }} min={0} placeholder="مثلاً 48" />
                  </Form.Item>
                </>
              ),
            },
          ]} />
        </Form>
      </Modal>
      {pdf.node}
    </div>
  );
}
